import {
  Component, Input, Output, EventEmitter,
  OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef
} from '@angular/core';
import { CommonModule } from '@angular/common';

export type CallPhase = 'incoming' | 'active' | 'hold' | 'declined';
export type CallType  = 'video' | 'audio';

function arcPos(angleDeg: number, R: number): { tx: number; ty: number } {
  const a = (angleDeg * Math.PI) / 180;
  return { tx: Math.round(R * Math.sin(a)), ty: Math.round(R * -Math.cos(a)) };
}

const START = 195, STEP = 38;
const ANGLES = [0,1,2,3,4].map(i => START + i * STEP);

const INCOMING_BTNS = [
  { id:'in-decline',  ...arcPos(ANGLES[0],138), delay:0,   icon:'call_end',          label:'Decline',    color:'#ffb4ab',            bg:'#93000a',              pulseClass:'pulse-red'   },
  { id:'in-audio',    ...arcPos(ANGLES[1],138), delay:55,  icon:'mic',               label:'Audio Only', color:'#bbc3ff',            bg:'#303f9f',              pulseClass:''            },
  { id:'in-hold',     ...arcPos(ANGLES[2],138), delay:90,  icon:'pause_circle',      label:'Hold',       color:'#44d8f1',            bg:'#1a2a4a',              pulseClass:'pulse-blue'  },
  { id:'in-facetime', ...arcPos(ANGLES[3],138), delay:125, icon:'videocam',          label:'FaceTime',   color:'rgba(57,255,20,0.9)',bg:'#1a5c00',              pulseClass:'pulse-green' },
  { id:'in-myroon',   ...arcPos(ANGLES[4],138), delay:160, icon:'spatial_audio_off', label:'MyRoon',     color:'#cebdff',            bg:'#1a0a35',              pulseClass:''            },
];

const ACTIVE_BTNS = [
  { id:'ac-mute',    ...arcPos(ANGLES[0],155), delay:0,   icon:'mic',              iconOff:'mic_off',    label:'Mute',    labelOff:'Unmute',      color:'#c6c5d4', colorOn:'#ffb4ab', bg:'#222941', bgOn:'rgba(147,0,10,.4)'   },
  { id:'ac-speaker', ...arcPos(ANGLES[1],155), delay:55,  icon:'volume_up',        iconOff:'volume_off', label:'Speaker', labelOff:'Speaker Off', color:'#c6c5d4', colorOn:'#44d8f1', bg:'#222941', bgOn:'rgba(68,216,241,.2)' },
  { id:'ac-flip',    ...arcPos(ANGLES[2],155), delay:90,  icon:'flip_camera_ios',  iconOff:null,         label:'Flip',    labelOff:null,          color:'#c6c5d4', colorOn:'#c6c5d4', bg:'#222941', bgOn:'#222941'             },
  { id:'ac-chat',    ...arcPos(ANGLES[3],155), delay:125, icon:'chat',             iconOff:null,         label:'Chat',    labelOff:null,          color:'#c6c5d4', colorOn:'#c6c5d4', bg:'#222941', bgOn:'#222941'             },
  { id:'ac-myroon',  ...arcPos(ANGLES[4],155), delay:160, icon:'spatial_audio_off',iconOff:null,         label:'MyRoon',  labelOff:null,          color:'#cebdff', colorOn:'#cebdff', bg:'#1a0a35', bgOn:'#2a1a45'             },
];

const EQ_DELAYS  = [0.1,0.3,0.2,0.5,0.4,0.6,0.2,0.4,0.7,0.3,0.1];
const EQ_HEIGHTS = [10,22,14,30,18,26,10,18,30,14,8];

@Component({
  selector: 'app-call-bubble',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './call-bubble.component.html',
  styleUrls:   ['./call-bubble.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CallBubbleComponent implements OnInit, OnDestroy {

  @Input() callerName = 'Captain Vane';
  @Input() callerSub  = 'Encrypted Abyssal Channel';
  @Input() callerImg  = 'https://lh3.googleusercontent.com/aida-public/AB6AXuArPoBjdy8loWImZXmE846bZozpB4UtAmYpxhdvni9vT0OSDRYtq0bfYw2AWCzUcCx11oz8B5s3bnUr2MiUWj2OKzLTz_vyfglGYP2qiAG1EAc8eyBwBXBI-MtzOYUedwXIkinRCP2aUc6GGwq1ZmnYfr3G9zQDK2MhIMplp_7DPfz3PBMGik7Bk01u0-s6kgeScXm0DREEteeKW3rACZiWehRaNdf5XNWaEBHgU4enWbKew7I6koB4zEcmvQZ_tevF6RZZ1ma6nNM';
  @Input() callType: CallType = 'video';
  @Input() signal = 'Sub-Sonic';

  @Output() declined = new EventEmitter<void>();
  @Output() accepted = new EventEmitter<CallType>();
  @Output() held     = new EventEmitter<void>();
  @Output() myRoon   = new EventEmitter<void>();
  @Output() ended    = new EventEmitter<void>();

  phase: CallPhase = 'incoming';
  menuOpen   = false;
  activeType: CallType | null = null;
  muted      = false;
  speakerOn  = false;
  flipped    = false;
  elapsed    = 0;

  incomingBtns = INCOMING_BTNS;
  activeBtns   = ACTIVE_BTNS;
  eqBars = EQ_DELAYS.map((delay, i) => ({ delay, height: EQ_HEIGHTS[i] }));

  private autoClose: any = null;
  private ticker:    any = null;

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.menuOpen = false;
    this.cdr.detectChanges();
  }

  get phaseLabel(): string {
    switch (this.phase) {
      case 'incoming': return this.callType === 'video' ? 'Incoming FaceTime' : 'Incoming Call';
      case 'active':   return this.activeType === 'video' ? 'FaceTime Active' : 'Voice Call Active';
      case 'hold':     return 'On Hold';
      default:         return 'Call Declined';
    }
  }
  get subLabel():    string { return this.phase === 'active' ? (this.activeType === 'video' ? 'Video Connected' : 'Audio Connected') : this.callerSub; }
  get statusText():  string {
    if (this.phase === 'active')   return `Active · ${this.timerDisplay}`;
    if (this.phase === 'hold')     return 'Holding…';
    if (this.phase === 'declined') return 'Missed Call';
    return `Incoming · ${this.signal}`;
  }
  get timerDisplay(): string {
    const m = String(Math.floor(this.elapsed / 60)).padStart(2,'0');
    const s = String(this.elapsed % 60).padStart(2,'0');
    return `${m}:${s}`;
  }
  get incomingOpen(): boolean { return this.menuOpen && this.phase === 'incoming'; }
  get activeOpen():   boolean { return this.menuOpen && this.phase === 'active'; }
  get pulseActive():  boolean { return this.phase === 'incoming' || this.phase === 'active'; }
  get showEnd():      boolean { return this.phase === 'active'; }
  get showHint():     boolean { return this.phase === 'incoming' || this.phase === 'active'; }
  get hintText():     string  { return this.phase === 'active' ? 'Tap to show controls' : 'Tap bubble to answer'; }
  get bubbleGlow():   string  {
    switch (this.phase) {
      case 'active':   return '0 0 48px rgba(206,189,255,.55),0 8px 40px rgba(0,0,0,.8)';
      case 'hold':     return '0 0 28px rgba(68,216,241,.4),0 8px 40px rgba(0,0,0,.8)';
      case 'declined': return 'none';
      default:         return '0 0 32px rgba(206,189,255,.4),0 8px 40px rgba(0,0,0,.7)';
    }
  }
  get bubbleTransform(): string {
    if (this.phase === 'declined') return 'scale(0.72)';
    if (this.flipped)              return 'scaleX(-1)';
    return 'scale(1)';
  }
  get progressDuration(): string { return this.phase === 'active' ? '4s' : '2.8s'; }

  onBubbleTap(): void {
    if (this.phase !== 'incoming' && this.phase !== 'active') return;
    this.menuOpen ? this.closeMenu() : this.openMenu(this.phase === 'active' ? 4000 : 2800);
  }

  onDecline(): void      { this.closeMenu(); this.phase = 'declined'; this.declined.emit(); this.cdr.markForCheck(); }
  onAccept(t: CallType): void { this.closeMenu(); this.activeType = t; this.phase = 'active'; this.startTimer(); this.accepted.emit(t); this.cdr.markForCheck(); }
  onHold(): void         { this.closeMenu(); this.phase = 'hold'; this.held.emit(); this.cdr.markForCheck(); }
  onMyRoon(): void       { this.closeMenu(); this.myRoon.emit(); }
  onEnd(): void          { this.closeMenu(); this.stopTimer(); this.phase = 'declined'; this.ended.emit(); this.cdr.markForCheck(); }
  onMute(): void         { this.muted     = !this.muted;     this.cdr.markForCheck(); }
  onSpeaker(): void      { this.speakerOn = !this.speakerOn; this.cdr.markForCheck(); }
  onFlip(): void         { this.flipped   = !this.flipped;   this.cdr.markForCheck(); }
  onChat(): void         { }

  handleIncoming(id: string): void {
    switch (id) {
      case 'in-decline':  this.onDecline();       break;
      case 'in-audio':    this.onAccept('audio'); break;
      case 'in-hold':     this.onHold();          break;
      case 'in-facetime': this.onAccept('video'); break;
      case 'in-myroon':   this.onMyRoon();        break;
    }
  }

  handleActive(id: string): void {
    switch (id) {
      case 'ac-mute':    this.onMute();    break;
      case 'ac-speaker': this.onSpeaker(); break;
      case 'ac-flip':    this.onFlip();   break;
      case 'ac-chat':    this.onChat();   break;
      case 'ac-myroon':  this.onMyRoon(); break;
    }
  }

  getControlIcon(btn:  any): string { return (btn.id==='ac-mute'&&this.muted)||(btn.id==='ac-speaker'&&this.speakerOn) ? (btn.iconOff??btn.icon)   : btn.icon;  }
  getControlLabel(btn: any): string { return (btn.id==='ac-mute'&&this.muted)||(btn.id==='ac-speaker'&&this.speakerOn) ? (btn.labelOff??btn.label) : btn.label; }
  getControlColor(btn: any): string { return (btn.id==='ac-mute'&&this.muted)||(btn.id==='ac-speaker'&&this.speakerOn) ? btn.colorOn : btn.color; }
  getControlBg(btn:    any): string { return (btn.id==='ac-mute'&&this.muted)||(btn.id==='ac-speaker'&&this.speakerOn) ? btn.bgOn    : btn.bg;    }

  btnTransform(btn: any, visible: boolean): string {
    return visible
      ? `translate(calc(-50% + ${btn.tx}px), calc(-50% + ${btn.ty}px)) scale(1)`
      : 'translate(-50%,-50%) scale(0.4)';
  }
  btnTransition(delay: number): string {
    return `opacity .3s ${delay/1000}s, transform .5s cubic-bezier(0.25, 1, 0.5, 1) ${delay/1000}s`;
  }
  eqAnimation(delay: number): string {
    return this.phase === 'active' && !this.muted
      ? `cwEq 1.2s ease-in-out ${delay}s infinite alternate`
      : 'none';
  }

  private startTimer(): void {
    this.elapsed = 0;
    this.ticker = setInterval(() => { this.elapsed++; this.cdr.markForCheck(); }, 1000);
  }
  private stopTimer(): void { if (this.ticker) { clearInterval(this.ticker); this.ticker = null; } }
  private openMenu(ms: number): void {
    this.menuOpen = true;
    clearTimeout(this.autoClose);
    this.autoClose = setTimeout(() => { this.menuOpen = false; this.cdr.markForCheck(); }, ms);
    this.cdr.markForCheck();
  }
  private closeMenu(): void { this.menuOpen = false; clearTimeout(this.autoClose); this.cdr.markForCheck(); }

  ngOnDestroy(): void { this.closeMenu(); this.stopTimer(); }
  trackById(_: number, item: any): string { return item.id; }
}
